import { formatDate, formatCurrency, formatNumber } from './formatters';

// Export to CSV (fully functional)
export function exportToCSV(data, filename = 'export', options = {}) {
    if (!data || !Array.isArray(data) || data.length === 0) {
        console.warn('No data to export');
        return;
    }

    const {
        columns = null, // Specific columns to export
        headers = null, // Custom headers
        delimiter = ',',
        includeTimestamp = true,
    } = options;

    // Get columns from first item if not specified
    const cols = columns || Object.keys(data[0]).filter(key =>
        typeof data[0][key] !== 'object' || data[0][key] === null
    );

    // Create header row
    const headerRow = headers || cols.map(col => {
        // Convert camelCase to Title Case
        return col.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase());
    });

    // Create data rows
    const rows = data.map(item => {
        return cols.map(col => {
            let value = item[col];

            // Handle null/undefined
            if (value === null || value === undefined) return '';

            // Handle arrays/objects
            if (typeof value === 'object') {
                return JSON.stringify(value);
            }

            // Escape quotes and wrap in quotes if contains delimiter
            value = String(value);
            if (value.includes(delimiter) || value.includes('"') || value.includes('\n')) {
                value = `"${value.replace(/"/g, '""')}"`;
            }

            return value;
        });
    });

    // Combine header and data
    const csvContent = [
        headerRow.join(delimiter),
        ...rows.map(row => row.join(delimiter))
    ].join('\n');

    // Generate filename with timestamp
    const timestamp = includeTimestamp
        ? `_${new Date().toISOString().slice(0, 10)}`
        : '';
    const fullFilename = `${filename}${timestamp}.csv`;

    // Create and download file
    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', fullFilename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    URL.revokeObjectURL(url);

    return { success: true, filename: fullFilename, rowCount: data.length };
}

// Export to JSON
export function exportToJSON(data, filename = 'export') {
    if (!data) {
        console.warn('No data to export');
        return;
    }

    const jsonContent = JSON.stringify(data, null, 2);
    const timestamp = new Date().toISOString().slice(0, 10);
    const fullFilename = `${filename}_${timestamp}.json`;

    const blob = new Blob([jsonContent], { type: 'application/json' });
    const url = URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', fullFilename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    URL.revokeObjectURL(url);

    return { success: true, filename: fullFilename };
}

// Export to Excel (placeholder - would need xlsx library)
export function exportToExcel(data, filename = 'export') {
    console.warn('Excel export requires xlsx library. Using CSV export instead.');
    return exportToCSV(data, filename);
}

// Export to PDF (placeholder - would need jspdf library)
export function exportToPDF(data, filename = 'export') {
    console.warn('PDF export requires jspdf library. Functionality not implemented.');
    return { success: false, message: 'PDF export not implemented' };
}

// Generate report summary
export function generateReportSummary(data, options = {}) {
    const {
        title = 'Report Summary',
        dateRange = null,
    } = options;

    if (!data || !Array.isArray(data) || data.length === 0) {
        return { title, totalRecords: 0, message: 'No data available' };
    }

    const summary = {
        title,
        generatedAt: new Date().toISOString(),
        totalRecords: data.length,
    };

    if (dateRange) {
        summary.dateRange = dateRange;
    }

    // Calculate common metrics if applicable
    if (data[0].total !== undefined) {
        const totals = data.map(item => Number(item.total) || 0);
        summary.totalValue = totals.reduce((a, b) => a + b, 0);
        summary.averageValue = summary.totalValue / data.length;
        summary.minValue = Math.min(...totals);
        summary.maxValue = Math.max(...totals);
    }

    // Count by status if applicable
    if (data[0].status !== undefined) {
        summary.statusBreakdown = data.reduce((acc, item) => {
            acc[item.status] = (acc[item.status] || 0) + 1;
            return acc;
        }, {});
    }

    return summary;
}

// Prepare export data with custom formatting
export function prepareExportData(data, columnConfig) {
    if (!data || !Array.isArray(data)) return [];

    return data.map(item => {
        const row = {};

        columnConfig.forEach(({ key, label, format }) => {
            let value = item[key];

            if (format) {
                switch (format) {
                    case 'currency':
                        value = formatCurrency(value);
                        break;
                    case 'number':
                        value = formatNumber(value);
                        break;
                    case 'date':
                        value = formatDate(value, 'medium');
                        break;
                    case 'percentage':
                        value = `${value}%`;
                        break;
                    default:
                        break;
                }
            }

            row[label || key] = value;
        });

        return row;
    });
}

export default {
    exportToCSV,
    exportToJSON,
    exportToExcel,
    exportToPDF,
    generateReportSummary,
    prepareExportData,
};
